import dayjs from "dayjs";

const toMinute = (value) => dayjs(value).startOf("minute");

const parseDateTime = (dateStr, timeStr) => {
  if (!timeStr) return null;
  const timeValue = String(timeStr).trim();
  if (!timeValue) return null;
  const hasFullDate = timeValue.includes("-") || timeValue.includes("T");
  if (hasFullDate) return toMinute(timeValue);
  if (!dateStr) return null;
  return toMinute(`${dateStr} ${timeValue}`);
};

const hasMeaningfulStamp = (value) =>
  value !== null && value !== undefined && String(value).trim() !== "";

/** صف حضور فعلي (ليس إجازة متزامنة type>0) */
function isRealAttendanceRow(row) {
  if (!row) return false;
  if (Number(row?.canceled) === 1) return false;
  return !(Number(row?.type) > 0);
}

/**
 * شرائح الحضور الفعلية لصف واحد (نفس ترتيب hasVacationOverlapWithAttendance).
 * @returns {{ start: import("dayjs").Dayjs, end: import("dayjs").Dayjs }[]}
 */
function segmentsForRow(row, options) {
  const getSegments = options.getSegments;
  const preferSegmentsWhenPresent = Boolean(options.preferSegmentsWhenPresent);
  const rowDate = row?.date;

  if (typeof getSegments === "function" && preferSegmentsWhenPresent) {
    const segments = getSegments(row) || [];
    const out = [];
    for (const seg of segments) {
      const segStart = toMinute(seg?.in);
      const segEnd = toMinute(seg?.out);
      if (
        segStart?.isValid() &&
        segEnd?.isValid() &&
        segEnd.isAfter(segStart)
      ) {
        out.push({ start: segStart, end: segEnd });
      }
    }
    if (out.length > 0) return out;
  }

  const hasIn = hasMeaningfulStamp(row?.attendance_time);
  const hasOut = hasMeaningfulStamp(row?.leave_time);
  if (hasIn && hasOut) {
    const attStart = parseDateTime(rowDate, row.attendance_time);
    const attEnd = parseDateTime(rowDate, row.leave_time);
    if (
      attStart?.isValid() &&
      attEnd?.isValid() &&
      attEnd.isAfter(attStart)
    ) {
      return [{ start: attStart, end: attEnd }];
    }
  }
  return [];
}

/** دمج شرائح متطابلة بالدقيقة (تفادي ازدواجية API بين الرئيسي وـ extra_attendance) */
function dedupeSegmentsByMinute(segments) {
  const seen = new Set();
  const out = [];
  for (const s of segments) {
    if (!s?.start?.isValid?.() || !s?.end?.isValid?.()) continue;
    const key = `${s.start.format("YYYY-MM-DD HH:mm")}_${s.end.format("YYYY-MM-DD HH:mm")}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
}

/** كل شرائح الحضور الفعلية في يوم تقويمي واحد */
function collectRealSegmentsForDate(rowList, dateStr, options) {
  const list = Array.isArray(rowList) ? rowList : [];
  const all = [];
  for (const row of list) {
    if (!isRealAttendanceRow(row)) continue;
    if (String(row?.date) !== String(dateStr)) continue;
    all.push(...segmentsForRow(row, options));
  }
  return dedupeSegmentsByMinute(all);
}

/** نهاية/نهايات الدوام الرسمية من getShiftEndForDate (قيمة واحدة أو مصفوفة) */
function normalizeShiftEndCandidates(raw, dateStr) {
  if (raw == null || raw === "") return [];
  const arr = Array.isArray(raw) ? raw : [raw];
  const out = [];
  const seen = new Set();
  for (const item of arr) {
    if (item == null || item === "") continue;
    let d = null;
    if (typeof item === "object" && typeof item?.isValid === "function") {
      const t = toMinute(item);
      d = t.isValid() ? t : null;
    } else {
      const s = String(item).trim();
      if (!s) continue;
      const timeOnly =
        !s.includes("-") &&
        !s.includes("T") &&
        s.length <= 12 &&
        /^\d{1,2}:\d{2}/.test(s);
      d = timeOnly ? toMinute(`${dateStr} ${s}`) : toMinute(s);
    }
    if (!d?.isValid?.()) continue;
    const k = d.format("YYYY-MM-DD HH:mm");
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(d);
  }
  return out;
}

/**
 * وقت خروج مسجّل يطابق نهاية الدوام الرسمية (مع تسامح دقيقتين أو تطابق HH:mm)
 * لأن السجلات قد تختلف ثوانٍ/تنسيقًا عن daily_shift_ranges.
 */
function segmentEndMatchesOfficialClose(endMin, shiftCandidates, dateStr) {
  if (!endMin?.isValid?.() || !shiftCandidates.length) return false;
  for (const shiftEnd of shiftCandidates) {
    if (!shiftEnd.isValid()) continue;
    const diffMin = Math.abs(endMin.diff(shiftEnd, "minute"));
    if (diffMin <= 2) return true;
    if (
      endMin.format("HH:mm") === shiftEnd.format("HH:mm") &&
      (endMin.format("YYYY-MM-DD") === dateStr ||
        shiftEnd.format("YYYY-MM-DD") === dateStr ||
        endMin.format("YYYY-MM-DD") === shiftEnd.format("YYYY-MM-DD"))
    ) {
      return true;
    }
  }
  return false;
}

/**
 * شريحة واحدة فقط في اليوم وتنتهي عند نهاية الدوام الرسمية (تقدير بدون بصمة خروج) —
 * لا نمنع الإجازة إلا إذا كانت تبدأ من وقت الدخول المسجّل فصاعدًا (لا نسمح بإجازة «قبل الوصول»).
 */
function shouldIgnoreOverlapDueToSyntheticShiftEnd(
  dateStr,
  segmentStart,
  segmentEnd,
  reqStart,
  rowList,
  options,
) {
  const getter = options.getShiftEndForDate;
  if (typeof getter !== "function") return false;

  const rawEnd = getter(dateStr);
  const shiftCandidates = normalizeShiftEndCandidates(rawEnd, dateStr);
  if (!shiftCandidates.length) return false;

  const endMin = toMinute(segmentEnd);
  if (!endMin.isValid()) return false;
  if (!segmentEndMatchesOfficialClose(endMin, shiftCandidates, dateStr)) {
    return false;
  }

  const daySegments = collectRealSegmentsForDate(rowList, dateStr, options);
  if (daySegments.length !== 1) return false;

  const segS = toMinute(segmentStart);
  if (!segS.isValid() || !reqStart.isValid()) return false;
  if (reqStart.isBefore(segS, "minute")) return false;

  return true;
}

/**
 * تداخل فترة الإجازة [reqStart, reqEnd] مع «وقت العمل الفعلي» [segStart, segEnd) (نصف مفتوح من جهة الخروج):
 * - قبل أول دخول ومن لحظة الخروج حتى نهاية الدوام لا يُعتبر تعارضًا مع تلك الشريحة.
 * - يتعارض فقط ما يقطع (segStart, segEnd) — أي داخل الفترة بين الدخول والخروج بدقة الدقيقة.
 */
function vacationOverlapsActualWorkInterval(reqStart, reqEnd, segStart, segEnd) {
  return (
    reqStart.isBefore(segEnd, "minute") && reqEnd.isAfter(segStart, "minute")
  );
}

/**
 * @param {string} startDateTime
 * @param {string} endDateTime
 * @param {object[]} rows
 * @param {object} [options]
 * @param {(row: object) => { in?: string, out?: string }[]} [options.getSegments]
 * @param {boolean} [options.preferSegmentsWhenPresent]
 * @param {(dateStr: string) => string|string[]|import("dayjs").Dayjs|null|undefined} [options.getShiftEndForDate] نهاية الدوام لذلك اليوم (يمكن إرجاع مصفوفة: end + allowed_end)
 */
export function hasVacationOverlapWithAttendance(
  startDateTime,
  endDateTime,
  rows,
  options = {},
) {
  const reqStart = toMinute(startDateTime);
  const reqEnd = toMinute(endDateTime);
  if (!reqStart.isValid() || !reqEnd.isValid() || !reqEnd.isAfter(reqStart)) {
    return false;
  }

  const rowList = Array.isArray(rows) ? rows : [];
  const getSegments = options.getSegments;
  const preferSegmentsWhenPresent = Boolean(options.preferSegmentsWhenPresent);

  for (const row of rowList) {
    if (!row) continue;
    if (!isRealAttendanceRow(row)) continue;

    const rowDate = row?.date;
    let hasUsableSegments = false;

    if (typeof getSegments === "function") {
      const segments = getSegments(row) || [];
      for (const seg of segments) {
        const segStart = toMinute(seg?.in);
        const segEnd = toMinute(seg?.out);
        if (
          segStart?.isValid() &&
          segEnd?.isValid() &&
          segEnd.isAfter(segStart)
        ) {
          hasUsableSegments = true;
          if (vacationOverlapsActualWorkInterval(reqStart, reqEnd, segStart, segEnd)) {
            if (
              shouldIgnoreOverlapDueToSyntheticShiftEnd(
                rowDate,
                segStart,
                segEnd,
                reqStart,
                rowList,
                options,
              )
            ) {
              continue;
            }
            return true;
          }
        }
      }
      if (preferSegmentsWhenPresent && hasUsableSegments) {
        continue;
      }
      if (segments.length > 0 && !hasUsableSegments) {
        // شرائح ناقصة: نكمل على الحقول الرئيسية
      }
    }

    const hasIn = hasMeaningfulStamp(row?.attendance_time);
    const hasOut = hasMeaningfulStamp(row?.leave_time);

    if (hasIn && hasOut) {
      const attStart = parseDateTime(rowDate, row.attendance_time);
      const attEnd = parseDateTime(rowDate, row.leave_time);
      if (
        attStart?.isValid() &&
        attEnd?.isValid() &&
        attEnd.isAfter(attStart) &&
        vacationOverlapsActualWorkInterval(reqStart, reqEnd, attStart, attEnd)
      ) {
        if (
          shouldIgnoreOverlapDueToSyntheticShiftEnd(
            rowDate,
            attStart,
            attEnd,
            reqStart,
            rowList,
            options,
          )
        ) {
          continue;
        }
        return true;
      }
      continue;
    }

    // دخول فقط بدون خروج: لا تُنشئ فترة «داخل بصمة» (نقطة حضور فقط — يُسمح بإجازة 7:00–7:25 و7:25–14:00 مثل الباك اند)
    if (hasIn && !hasOut) {
      continue;
    }

    if (hasOut) {
      const attStart = toMinute(`${rowDate} 00:00:00`);
      const attEnd = parseDateTime(rowDate, row.leave_time);
      if (
        attStart?.isValid() &&
        attEnd?.isValid() &&
        vacationOverlapsActualWorkInterval(reqStart, reqEnd, attStart, attEnd)
      ) {
        return true;
      }
    }
  }

  return false;
}
